import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { differenceInDays } from 'date-fns';
import {
  ArrowUpDown, ArrowUp, ArrowDown, Ban, ExternalLink, Trash2, Pencil, Printer, Link as LinkIcon,
  Settings2, Download, Copy, CopyPlus, FilterX,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils';
import { AccountFilterCombobox } from './AccountFilterCombobox';
import { QuoteStatusBadge } from './QuoteStatusBadge';
import { useSession } from '@/hooks/useSession';
import { useHasQuotePermission } from '../hooks/useQuotePermission';
import { QUOTE_PERMISSIONS } from '../permissions';
import { isQuoteExpired, isExpirationApproaching } from '../types';
import { escapeCSVValue } from '../utils/csvExport';
import { getQuoteHtmlUrlV4 } from '../api';
import type { Quote, QuotesFilters } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface FilterOption {
  value: string;
  label?: string;
  labelKey?: string;
}

interface QuoteColumnDef {
  key: string;
  labelKey: string;
  defaultVisible?: boolean;
  sortKey?: QuotesFilters['sortKey'];
  filterType?: 'text' | 'date-range' | 'select' | 'searchable-select' | 'single-select';
  filterOptions?: FilterOption[];
  getValue: (quote: Quote) => string | number | null;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  permission?: string;
  headerClassName?: string;
  cellClassName?: string;
}

// =============================================================================
// Filter option constants
// =============================================================================

const STATUS_OPTIONS = [
  'preparation', 'sent', 'waiting-po', 'approved', 'inprogress',
  'invoice-ready', 'partially-invoiced', 'invoiced', 'cancelled', 'rejected', 'expired',
];

const DEAL_TYPE_OPTIONS = ['newbusiness', 'existingbusiness', 'recurring', 'rfp'];

const STATUS_FILTER_OPTIONS = STATUS_OPTIONS.map((s) => ({ value: s, labelKey: `status.${s}` }));
const DEAL_TYPE_FILTER_OPTIONS = DEAL_TYPE_OPTIONS.map((d) => ({ value: d, labelKey: `dealTypes.${d}` }));

async function printHtmlDocument(html: string, fileName: string) {
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.style.visibility = 'hidden';
  document.body.appendChild(frame);

  const previousTitle = document.title;
  document.title = fileName;

  const frameDocument = frame.contentDocument;
  if (!frameDocument || !frame.contentWindow) {
    document.title = previousTitle;
    frame.remove();
    throw new Error('Print frame could not be created');
  }

  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();

  await new Promise((resolve) => window.setTimeout(resolve, 1000));
  frame.contentWindow.focus();
  frame.contentWindow.print();
  window.focus();
  document.title = previousTitle;
  frame.remove();
}

// =============================================================================
// Column definitions
// =============================================================================

const COLUMNS: QuoteColumnDef[] = [
  {
    key: 'quoteNumber',
    labelKey: 'table.quoteNumber',
    defaultVisible: true,
    sortKey: 'quoteNumber',
    filterType: 'text',
    getValue: (q) => q.quoteNumber,
    headerClassName: 'w-24',
    cellClassName: 'font-medium',
  },
  {
    key: 'status',
    labelKey: 'table.status',
    defaultVisible: true,
    filterType: 'select',
    filterOptions: STATUS_FILTER_OPTIONS,
    getValue: (q) => q.status,
    headerClassName: 'w-28',
  },
  {
    key: 'createdAt',
    labelKey: 'table.created',
    defaultVisible: true,
    sortKey: 'createdAt',
    filterType: 'date-range',
    getValue: (q) => q.createdAt,
    headerClassName: 'w-28',
  },
  {
    key: 'accountName',
    labelKey: 'table.customer',
    defaultVisible: true,
    sortKey: 'accountName',
    filterType: 'searchable-select',
    getValue: (q) => q.accountName,
    cellClassName: 'max-w-40 truncate',
  },
  {
    key: 'accountNumber',
    labelKey: 'table.accountNumber',
    defaultVisible: true,
    filterType: 'text',
    getValue: (q) => q.accountNumber,
    cellClassName: 'text-muted-foreground text-sm',
  },
  {
    key: 'projectName',
    labelKey: 'table.project',
    defaultVisible: true,
    sortKey: 'projectName',
    filterType: 'text',
    getValue: (q) => q.projectName,
    cellClassName: 'max-w-40 truncate',
  },
  {
    key: 'salesManager',
    labelKey: 'table.salesManager',
    defaultVisible: true,
    filterType: 'single-select',
    getValue: (q) => q.salesManagerName,
    cellClassName: 'text-muted-foreground text-sm',
  },
  {
    key: 'total',
    labelKey: 'table.total',
    defaultVisible: true,
    sortKey: 'total',
    noFilter: true,
    getValue: (q) => parseFloat(q.total) || 0,
    align: 'right',
    headerClassName: 'w-28',
    cellClassName: 'font-medium',
  },
  {
    key: 'expirationDate',
    labelKey: 'table.expiration',
    defaultVisible: false,
    filterType: 'date-range',
    getValue: (q) => q.expirationDate,

    headerClassName: 'w-28',
  },
  {
    key: 'acceptedDate',
    labelKey: 'table.accepted',
    defaultVisible: false,
    sortKey: 'acceptedDate',
    filterType: 'date-range',
    getValue: (q) => q.acceptedDate,
    headerClassName: 'w-28',
  },
  {
    key: 'contractualManager',
    labelKey: 'table.contact',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.contractualManagerName,

    cellClassName: 'text-muted-foreground text-sm',
  },
  {
    key: 'poNumber',
    labelKey: 'table.poNumber',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.poNumber,

    cellClassName: 'text-sm',
  },
  {
    key: 'quoteDate',
    labelKey: 'table.quoteDate',
    defaultVisible: false,
    sortKey: 'date',
    filterType: 'date-range',
    getValue: (q) => q.date || q.createdDate,
    headerClassName: 'w-28',
  },
  {
    key: 'contract',
    labelKey: 'table.contract',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.contractName || q.contractNumber || null,

    cellClassName: 'text-sm max-w-[150px] truncate',
  },
  {
    key: 'ticket',
    labelKey: 'table.ticket',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.ticketName || null,
    cellClassName: 'text-sm',
  },
  {
    key: 'projectNumber',
    labelKey: 'table.projectNumber',
    defaultVisible: false,
    sortKey: 'projectNumber',
    filterType: 'text',
    getValue: (q) => q.projectNumber || null,
    cellClassName: 'text-sm',
  },
  {
    key: 'dealType',
    labelKey: 'table.dealType',
    defaultVisible: false,
    sortKey: 'dealType',
    filterType: 'select',
    filterOptions: DEAL_TYPE_FILTER_OPTIONS,
    getValue: (q) => q.dealType || null,

    cellClassName: 'text-sm',
  },
  {
    key: 'nextStep',
    labelKey: 'table.nextStep',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.nextStep || null,
    cellClassName: 'text-sm max-w-[200px] truncate',
  },
  {
    key: 'nextStepDeadline',
    labelKey: 'table.nextStepDeadline',
    defaultVisible: false,
    sortKey: 'nextStepDeadline',
    filterType: 'date-range',
    getValue: (q) => q.nextStepDeadline || null,
    headerClassName: 'w-28',
  },
  {
    key: 'subscription',
    labelKey: 'table.subscription',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.subscriptionName || q.subscriptionNumber || null,

    cellClassName: 'text-sm',
  },
  {
    key: 'invoiceNumbers',
    labelKey: 'table.invoiceNumbers',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.invoiceNumbers?.join(', ') || null,

    cellClassName: 'text-sm',
  },
  {
    key: 'taxes',
    labelKey: 'table.taxes',
    defaultVisible: false,
    noFilter: true,
    getValue: (q) => parseFloat(q.taxAmount) || 0,
    align: 'right',
    cellClassName: 'text-sm',
  },
  {
    key: 'billingAddress',
    labelKey: 'table.billingAddress',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.billingAddress || null,

    cellClassName: 'text-sm max-w-[200px] truncate',
  },
  {
    key: 'shippingAddress',
    labelKey: 'table.shippingAddress',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.shippingAddress || null,

    cellClassName: 'text-sm max-w-[200px] truncate',
  },
  {
    key: 'trackingNumbers',
    labelKey: 'table.trackingNumbers',
    defaultVisible: false,
    filterType: 'text',
    getValue: (q) => q.trackingNumbers?.join(', ') || null,
    cellClassName: 'text-sm',
  },
  {
    key: 'poFile',
    labelKey: 'table.poFile',
    defaultVisible: false,
    noFilter: true,
    getValue: (q) => q.poFile ? 'Yes' : '',
    cellClassName: 'text-sm',
  },
  {
    key: 'createdAtFull',
    labelKey: 'table.createdAtFull',
    defaultVisible: false,
    sortKey: 'createdAt',
    filterType: 'date-range',
    getValue: (q) => q.createdAt,
    headerClassName: 'w-36',
    cellClassName: 'text-sm',
  },
  {
    key: 'actions',
    labelKey: 'table.actions',
    defaultVisible: true,
    noFilter: true,
    getValue: () => '',
    headerClassName: 'w-28',
  },
];

// =============================================================================
// Column visibility persistence
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-quotes-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: QuotesFilters['sortKey'];
  currentSortKey: QuotesFilters['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: QuotesFilters['sortKey']) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent hover:text-inherit"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function getDeadlineClass(deadline: string | null | undefined): string {
  if (!deadline) return '';
  const diff = differenceInDays(new Date(deadline), new Date());
  if (diff < 0) return 'text-red-600 font-medium';
  if (diff <= 7) return 'text-yellow-600';
  return '';
}

async function handlePrintQuote(quote: Quote, t: (key: string) => string) {
  try {
    const response = await fetch(getQuoteHtmlUrlV4(quote.id), {
      credentials: 'include',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`Failed to load quote HTML (${response.status})`);
    const html = await response.text();
    await printHtmlDocument(
      html,
      `ATEK Quote ${quote.quoteNumber} ${quote.projectName || ''}.pdf`,
    );
  } catch (error) {
    toast.error(t('toast.pdfError'), {
      description: error instanceof Error ? error.message : undefined,
    });
  }
}

// =============================================================================
// QuotesTable
// =============================================================================

interface QuotesTableProps {
  quotes: Quote[];
  pageItemCount: number;
  total: number;
  sortKey: QuotesFilters['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: QuotesFilters['sortKey']) => void;
  onEdit?: (quote: Quote) => void;
  onDelete?: (quote: Quote) => void;
  onDuplicate?: (quote: Quote) => void;
  onDiscard?: (quote: Quote) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  highlightId?: string;
  columnFilters: Record<string, string>;
  selectedAccountId?: string;
  onSelectedAccountChange?: (accountId: string) => void;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearColumnFilters?: () => void;
  onLoadExportQuotes?: () => Promise<Quote[]>;
  isLoading?: boolean;
  searchableSelectOptions?: Record<string, FilterOption[]>;
  toolbarTitle?: ReactNode;
  toolbarControls?: ReactNode;
}

export function QuotesTable({
  quotes,
  pageItemCount,
  total,
  sortKey,
  sortDirection,
  onSort,
  onEdit,
  onDelete,
  onDuplicate,
  onDiscard,
  page,
  pageSize,
  onPageChange,
  highlightId,
  columnFilters,
  selectedAccountId = 'all',
  onSelectedAccountChange,
  onColumnFilterChange,
  onClearColumnFilters,
  onLoadExportQuotes,
  isLoading = false,
  searchableSelectOptions,
  toolbarTitle,
  toolbarControls,
}: QuotesTableProps) {
  const { t } = useTranslation('quotes');
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  const totalPages = Math.ceil(total / pageSize) || 1;
  const [isExporting, setIsExporting] = useState(false);

  const canPrint = useHasQuotePermission(QUOTE_PERMISSIONS.print);
  const canExport = useHasQuotePermission(QUOTE_PERMISSIONS.export);
  const canViewLinks = useHasQuotePermission(QUOTE_PERMISSIONS.viewLinks);

  // ---------------------------------------------------------------------------
  // Permission-based column filtering
  // ---------------------------------------------------------------------------
  const { user } = useSession();
  const userPermissions = useMemo(
    () => new Set(user?.permission_strings || []),
    [user?.permission_strings],
  );

  const permittedColumns = useMemo(
    () => COLUMNS.filter((col) => {
      if (!col.permission) return true;
      return userPermissions.has(col.permission);
    }),
    [userPermissions],
  );

  // ---------------------------------------------------------------------------
  // Column visibility
  // ---------------------------------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  const visibleColumns = useMemo(
    () => permittedColumns.filter((col) => isVisible(col.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permittedColumns, columnVisibility],
  );

  // ---------------------------------------------------------------------------
  // Highlight scroll
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (highlightId && highlightedRowRef.current) {
      setTimeout(() => {
        highlightedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [highlightId, quotes]);

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const loadQuotesForExport = async () => {
    if (!onLoadExportQuotes) return quotes;
    return onLoadExportQuotes();
  };

  const handleCopy = async () => {
    setIsExporting(true);
    try {
      const exportQuotes = await loadQuotesForExport();
      const exportCols = visibleColumns.filter((c) => !c.noFilter);
      const header = exportCols.map((c) => t(c.labelKey)).join('\t');
      const rows = exportQuotes.map((q) =>
        exportCols.map((c) => String(c.getValue(q) ?? '')).join('\t'),
      );
      await navigator.clipboard.writeText([header, ...rows].join('\n'));
      toast.success(t('table.copied'));
    } catch (error) {
      toast.error(t('toast.exportError'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCSV = async () => {
    setIsExporting(true);
    try {
      const exportQuotes = await loadQuotesForExport();
      const exportCols = visibleColumns.filter((c) => !c.noFilter);
      const header = exportCols.map((c) => escapeCSVValue(t(c.labelKey))).join(',');
      const rows = exportQuotes.map((q) =>
        exportCols.map((c) => escapeCSVValue(c.getValue(q))).join(','),
      );
      const csvContent = [header, ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quotes_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(t('toast.exportSuccess', { count: exportQuotes.length }));
    } catch (error) {
      toast.error(t('toast.exportError'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Column filter rendering
  // ---------------------------------------------------------------------------
  const renderColumnFilter = (col: QuoteColumnDef) => {
    if (col.noFilter) {
      return (
        <TableHead
          key={`filter-${col.key}`}
          className={cn('p-1', col.key === 'actions' && 'text-center')}
        >
          {col.key === 'actions' ? (
            <span className="block text-center text-xs font-normal text-muted-foreground">{showingText}</span>
          ) : null}
        </TableHead>
      );
    }

    if (col.key === 'accountName' && onSelectedAccountChange) {
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <AccountFilterCombobox
            value={selectedAccountId}
            onChange={onSelectedAccountChange}
            placeholder={t('filters.allCustomers', 'All Accounts')}
          />
        </TableHead>
      );
    }

    if (col.filterType === 'single-select') {
      const options = searchableSelectOptions?.[col.key] || [];
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <Select
            value={columnFilters[col.key] || '__all__'}
            onValueChange={(value) =>
              onColumnFilterChange(col.key, value === '__all__' ? '' : value)
            }
          >
            <SelectTrigger
              aria-label={`Filter ${t(col.labelKey)}`}
              className="h-7 w-full min-w-0 text-xs font-normal"
            >
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label ?? opt.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableHead>
      );
    }

    // Multi-select filter
    if (col.filterType === 'select' && col.filterOptions) {
      const selected: string[] = (() => {
        try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
        catch { return []; }
      })();

      const toggle = (opt: string) => {
        const next = selected.includes(opt)
          ? selected.filter((o) => o !== opt)
          : [...selected, opt];
        onColumnFilterChange(col.key, next.length ? JSON.stringify(next) : '');
      };

      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
                {selected.length ? `${selected.length} selected` : '—'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {col.filterOptions.map((opt) => (
                <DropdownMenuCheckboxItem
                  key={opt.value}
                  checked={selected.includes(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                >
                  {opt.label ?? t(opt.labelKey!)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableHead>
      );
    }

    // Date range filter
    if (col.filterType === 'date-range') {
      const raw = columnFilters[col.key] || '';
      const [from = '', to = ''] = raw.split('>>');
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <div className="flex flex-col gap-0.5">
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={from}
              onChange={(e) => onColumnFilterChange(col.key, `${e.target.value}>>${to}`)}
              title={t('filters.from')}
            />
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={to}
              onChange={(e) => onColumnFilterChange(col.key, `${from}>>${e.target.value}`)}
              title={t('filters.to')}
            />
          </div>
        </TableHead>
      );
    }

    // Text filter (default)
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[col.key] || ''}
          onChange={(e) => onColumnFilterChange(col.key, e.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  // ---------------------------------------------------------------------------
  // Cell rendering
  // ---------------------------------------------------------------------------
  const renderCell = (col: QuoteColumnDef, quote: Quote): React.ReactNode => {
    const raw = col.getValue(quote);

    switch (col.key) {
      case 'quoteNumber':
        return onEdit ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(quote); }}
            className="font-semibold text-atek-blue transition-colors hover:text-atek-blue-dark hover:underline"
          >
            {String(raw ?? '') || '—'}
          </button>
        ) : (
          <span className="font-semibold">{String(raw ?? '') || '—'}</span>
        );
      case 'status':
        return (
          <div className="flex items-center gap-2">
            <QuoteStatusBadge status={quote.status} />
            {isExpirationApproaching(quote) && !isQuoteExpired(quote) && (
              <Badge variant="warning">{t('table.expiringSoon')}</Badge>
            )}
          </div>
        );
      case 'total':
        return formatCurrency(parseFloat(quote.total) || 0, quote.currency || 'CAD');
      case 'taxes':
        return formatCurrency(parseFloat(quote.taxAmount) || 0, quote.currency || 'CAD');
      case 'accountName':
        return quote.accountId ? (
          <Link
            to={`/accounts/${quote.accountId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-atek-blue hover:text-atek-blue-dark hover:underline"
          >
            {String(raw ?? '') || '—'}
          </Link>
        ) : (
          String(raw ?? '') || '—'
        );
      case 'dealType':
        return raw ? t(`dealTypes.${raw}`, { defaultValue: String(raw) }) : '—';
      case 'nextStepDeadline':
        return (
          <span className={getDeadlineClass(quote.nextStepDeadline)}>
            {raw ? formatDate(String(raw)) : '—'}
          </span>
        );
      case 'poFile':
        return quote.poFile ? '📎' : '—';
      case 'createdAtFull':
        return raw ? formatDateTime(String(raw)) : '—';
      case 'actions': {
        const quoteLinks = [
          { label: t('table.hubspot'), href: quote.hubspotLink },
          { label: t('table.googleDrive'), href: quote.gdriveLink },
        ].filter((link): link is { label: string; href: string } => Boolean(link.href));

        return (
          <div className="mx-auto inline-flex h-8 items-center justify-center gap-0.5 rounded-md border border-transparent px-0.5 opacity-75 transition-opacity group-hover/quote-row:opacity-100">
            {canViewLinks && quoteLinks.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:bg-transparent hover:text-atek-blue"
                    onClick={(e) => e.stopPropagation()}
                    title={t('table.links')}
                  >
                    <LinkIcon className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{t('table.links')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {quoteLinks.map((link) => (
                    <DropdownMenuItem key={link.label} asChild>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex cursor-pointer items-center justify-between gap-3"
                      >
                        <span>{link.label}</span>
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                      </a>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {onEdit && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:bg-transparent hover:text-atek-blue"
                onClick={(e) => { e.stopPropagation(); onEdit(quote); }}
                title={t('table.editQuote')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDuplicate && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:bg-transparent hover:text-atek-blue"
                onClick={(e) => { e.stopPropagation(); onDuplicate(quote); }}
                title={t('table.duplicateQuote')}
              >
                <CopyPlus className="h-3.5 w-3.5" />
              </Button>
            )}
            {canPrint && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:bg-transparent hover:text-atek-blue"
                onClick={(e) => { e.stopPropagation(); void handlePrintQuote(quote, t); }}
                title="PDF"
              >
                <Printer className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDiscard && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:bg-transparent hover:text-warning"
                onClick={(e) => { e.stopPropagation(); onDiscard(quote); }}
                title={t('table.discardQuote')}
              >
                <Ban className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:bg-transparent hover:text-destructive hover:drop-shadow-[0_0_6px_hsl(var(--destructive)/0.65)]"
                onClick={(e) => { e.stopPropagation(); onDelete(quote); }}
                title={t('table.deleteQuote')}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        );
      }
      default:
        // Auto-format date columns
        if (col.filterType === 'date-range') {
          return raw ? formatDate(String(raw)) : '—';
        }
        return String(raw ?? '') || '—';
    }
  };

  const hasActiveColumnFilters = (
    Object.values(columnFilters).some(Boolean) ||
    (!!selectedAccountId && selectedAccountId !== 'all')
  );

  // ---------------------------------------------------------------------------
  // Showing text
  // ---------------------------------------------------------------------------
  const showingText = quotes.length < pageItemCount
    ? t('table.showingFiltered', { filtered: quotes.length, pageCount: pageItemCount })
    : t('table.showingOf', { count: pageItemCount, total });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Toolbar: title + filters + export + column toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
          {toolbarTitle}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {toolbarControls}
          {hasActiveColumnFilters && onClearColumnFilters && (
            <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
              <FilterX className="h-4 w-4 mr-1" />
              {t('table.clearFilters', 'Clear filters')}
            </Button>
          )}
          {canExport && (
            <>
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={isExporting}>
                <Copy className="h-4 w-4 mr-1" />
                {t('table.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCSV} disabled={isExporting}>
                <Download className="h-4 w-4 mr-1" />
                {t('table.csv')}
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" />
                {t('table.columns')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t('table.toggleColumns')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {permittedColumns.filter((c) => !c.noFilter).map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={isVisible(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                >
                  {t(col.labelKey)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="mt-2 overflow-x-auto">
        <Table>
          <TableHeader>
            {/* ROW 1: Filter inputs (always visible, only visible columns) */}
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col))}
            </TableRow>

            {/* ROW 2: Column labels + sort controls */}
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    'transition-colors hover:text-atek-blue',
                    col.headerClassName,
                    col.align === 'right' && 'text-right',
                    col.key === 'actions' && 'text-center',
                  )}
                >
                  {col.sortKey ? (
                    <SortableHeader
                      label={t(col.labelKey)}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={onSort}
                    />
                  ) : (
                    t(col.labelKey)
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {/* Loading state */}
            {isLoading && Array.from({ length: 10 }).map((_, i) => (
              <TableRow key={i}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}

            {/* Empty state */}
            {!isLoading && quotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                  {t('table.noResults')}
                </TableCell>
              </TableRow>
            )}

            {/* Data rows */}
            {!isLoading && quotes.map((quote) => (
              <TableRow
                key={quote.id}
                ref={quote.quoteNumber === highlightId ? highlightedRowRef : undefined}
                className={cn(
                  'group/quote-row border-l-2 border-l-transparent transition-colors hover:border-l-atek-blue hover:bg-atek-blue/5',
                  quote.quoteNumber === highlightId && 'bg-yellow-50 dark:bg-yellow-950/30 animate-pulse-once',
                )}
              >
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      'text-sm',
                      col.align === 'right' && 'text-right tabular-nums',
                      col.cellClassName,
                      col.key === 'actions' && 'text-center',
                    )}
                  >
                    {renderCell(col, quote)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('table.pageOf', { current: page + 1, total: totalPages })}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page === 0}>
              {t('table.previous')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1}>
              {t('table.next')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
